/**
 * Create a real one-page PDF download from the already-rendered WPDS preview.
 * The source form and joint illustration are images; all overlay text is drawn
 * from the visible draft. No external services, packages or credentials.
 * The output is always a DRAFT, not a CWB approval.
 */
export async function downloadWpdsPdf(doc: Document): Promise<void> {
  const page = doc.getElementById("wpdsSheet");
  if (!page) throw new Error("Open the WPDS preview before exporting.");
  const base = doc.getElementById("formBase") as HTMLImageElement | null;
  if (!base?.src) throw new Error("The WPDS form background is missing.");

  const images = Array.from(page.querySelectorAll<HTMLImageElement>("img"));
  await Promise.all(images.map(async img => {
    if (!img.complete) {
      await new Promise<void>((resolve, reject) => {
        img.addEventListener("load", () => resolve(), { once: true });
        img.addEventListener("error", () => reject(new Error("A WPDS image could not be loaded.")), { once: true });
      });
    }
    if (!img.naturalWidth || !img.naturalHeight) throw new Error("A WPDS drawing image is unavailable.");
  }));
  await doc.fonts?.ready;

  const pageRect = page.getBoundingClientRect();
  if (pageRect.width <= 0) throw new Error("The WPDS preview is not visible.");
  const sx = 1088 / pageRect.width;
  const sy = 1408 / pageRect.height;
  const canvas = doc.createElement("canvas");
  const scale = 2; // 192dpi rasterized form for readable letter-size PDFs
  canvas.width = 1088 * scale;
  canvas.height = 1408 * scale;
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) throw new Error("This browser does not support PDF rendering.");
  ctx.scale(scale, scale);
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, 1088, 1408);

  function box(el: Element) {
    const r = el.getBoundingClientRect();
    return {
      x: (r.left - pageRect.left) * sx,
      y: (r.top - pageRect.top) * sy,
      w: r.width * sx,
      h: r.height * sy
    };
  }
  // Render the actual form and selected joint image first.
  for (const img of images) {
    const rect = box(img);
    const computed = doc.defaultView?.getComputedStyle(img);
    if (rect.w <= 0 || rect.h <= 0 || computed?.display === "none") continue;
    const ratio = Math.min(rect.w / img.naturalWidth, rect.h / img.naturalHeight);
    const fit = computed?.objectFit === "contain";
    const w = fit ? img.naturalWidth * ratio : rect.w;
    const h = fit ? img.naturalHeight * ratio : rect.h;
    ctx.drawImage(img, rect.x + (rect.w - w) / 2, rect.y + (rect.h - h) / 2, w, h);
  }

  const textNodes = page.querySelectorAll<HTMLElement>(
    ".formEntry, .jointValues, .jointId, .drawCode, .drawNotes, .sourceWarn"
  );
  for (const el of textNodes) {
    const text = el.textContent?.trim();
    if (!text) continue;
    const r = box(el);
    if (!r.w || !r.h) continue;
    const style = doc.defaultView?.getComputedStyle(el);
    if (!style || style.display === "none" || style.visibility === "hidden") continue;
    const fontSize = Number.parseFloat(style.fontSize) || 13;
    const lineHeight = Number.parseFloat(style.lineHeight) || fontSize * 1.2;
    const weight = Number.parseInt(style.fontWeight, 10);
    ctx.font = (Number.isFinite(weight) && weight >= 600 || style.fontWeight === "bold" ? "bold " : "") + fontSize + "px Arial";
    ctx.fillStyle = style.color || "#111";
    ctx.textBaseline = "alphabetic";
    const pad = el.classList.contains("formEntry") ? 2 : 0;
    const avail = Math.max(1, r.w - pad * 2);
    const multiline = el.classList.contains("multiline") || text.includes("\n") || el.classList.contains("jointValues");
    const lines: string[] = [];
    for (const originalLine of text.split(/\r?\n/)) {
      if (!multiline) { lines.push(originalLine); continue; }
      let current = "";
      for (const word of originalLine.split(/\s+/)) {
        const candidate = current ? current + " " + word : word;
        if (current && ctx.measureText(candidate).width > avail) {
          lines.push(current);
          current = word;
        } else current = candidate;
      }
      lines.push(current);
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(r.x, r.y, r.w, r.h);
    ctx.clip();
    const center = style.justifyContent === "center" || style.textAlign === "center";
    ctx.textAlign = center ? "center" : "left";
    const x = center ? r.x + r.w / 2 : r.x + pad;
    const top = multiline ? r.y + Math.min(fontSize, lineHeight) : r.y + (r.h - fontSize) / 2 + fontSize * 0.82;
    for (let i = 0; i < lines.length; i++) {
      const y = top + i * lineHeight;
      if (y > r.y + r.h + 1) break;
      ctx.fillText(lines[i], x, y, avail);
    }
    ctx.restore();
  }

  // A permanent draft marking stays in the downloaded document.
  ctx.fillStyle = "#9b2525";
  ctx.font = "bold 13px Arial";
  ctx.textAlign = "center";
  ctx.fillText("DRAFT - NOT CWB APPROVED - VERIFY AGAINST APPLICABLE WPS / CSA REQUIREMENTS", 544, 1394, 1020);

  const jpeg = canvas.toDataURL("image/jpeg", 0.94).split(",")[1];
  const imageBytes = Uint8Array.from(atob(jpeg), char => char.charCodeAt(0));
  const enc = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [0];
  let position = 0;
  const append = (value: string | Uint8Array) => {
    const bytes = typeof value === "string" ? enc.encode(value) : value;
    chunks.push(bytes);
    position += bytes.length;
  };
  const obj = (num: number, content: string) => {
    offsets[num] = position;
    append(num + " 0 obj\n" + content + "\nendobj\n");
  };
  append("%PDF-1.4\n");
  obj(1, "<< /Type /Catalog /Pages 2 0 R >>");
  obj(2, "<< /Type /Pages /Kids [3 0 R] /Count 1 >>");
  obj(3, "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>");
  offsets[4] = position;
  append("4 0 obj\n<< /Type /XObject /Subtype /Image /Width " + canvas.width +
    " /Height " + canvas.height + " /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length " +
    imageBytes.length + " >>\nstream\n");
  append(imageBytes);
  append("\nendstream\nendobj\n");
  const content = "q\n612 0 0 792 0 0 cm\n/Im0 Do\nQ\n";
  obj(5, "<< /Length " + enc.encode(content).length + " >>\nstream\n" + content + "endstream");
  const startxref = position;
  append("xref\n0 6\n0000000000 65535 f \n");
  for (let i = 1; i <= 5; i++) append(String(offsets[i]).padStart(10, "0") + " 00000 n \n");
  append("trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n" + startxref + "\n%%EOF\n");
  const pdf = new Uint8Array(position);
  let at = 0;
  for (const bytes of chunks) { pdf.set(bytes, at); at += bytes.length; }
  const url = URL.createObjectURL(new Blob([pdf.buffer as ArrayBuffer], { type: "application/pdf" }));
  const id = (doc.getElementById("wpds") as HTMLInputElement | null)?.value || "wpds";
  const filename = (id.replace(/[^a-zA-Z0-9_-]/g, "-").slice(0, 64) || "wpds") + "-DRAFT.pdf";
  const anchor = doc.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  doc.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
